import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, qs } from '../lib/api.js';
import { BrandSelect, useBrands } from '../lib/brands.js';
import type { Rights } from '../lib/session.js';
import {
  ConfirmDialog,
  Dialog,
  EmptyState,
  ErrorState,
  Field,
  Loading,
  Panel,
  StatusBadge,
} from '../components/ui.js';

interface Item {
  brand: string;
  template: string;
  code: string;
  language: string;
  emailId: number;
}
interface Coverage {
  brand: string;
  template: string;
  code: string;
  expected: number;
  filled: number;
  missing: string[];
}
interface Listing {
  items: Item[];
  templates: Array<{ template: string; code: string }>;
  coverage: Coverage[];
}
interface CsvRow {
  row: number;
  brand: string;
  template: string;
  language: string;
  emailId: number | null;
  status: 'new' | 'changed' | 'same' | 'error';
  error?: string;
}
interface Verification {
  emailId: number;
  exists: boolean;
  subject: string | null;
  used: string[];
  missing: string[];
  unused: string[];
  missingOneOf: string[] | null;
  ok: boolean;
}

const brandLabel = (code: string): string => (code === '*' ? 'Všechny značky' : code);
const ROW_STATUS: Record<CsvRow['status'], { tone: 'ok' | 'warn' | 'bad' | 'neu'; text: string }> =
  {
    new: { tone: 'ok', text: 'nové' },
    changed: { tone: 'warn', text: 'změna' },
    same: { tone: 'neu', text: 'beze změny' },
    error: { tone: 'bad', text: 'chyba' },
  };

/** Záložka „Šablony e-shopu“: mapování šablona × jazyk → `email_id` u SmartEmailingu (D-213). */
export function PassthroughTemplates({ rights }: { rights: Rights }): JSX.Element {
  const client = useQueryClient();
  const brands = useBrands();
  const [brand, setBrand] = useState('');
  const [editing, setEditing] = useState<Partial<Item> | null>(null);
  const [deleting, setDeleting] = useState<Item | null>(null);
  const [importing, setImporting] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [verified, setVerified] = useState<{ item: Item; result: Verification } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const mayWrite = rights.write('transactional') && rights.can('map_templates');
  const activeBrand = brand || brands.data?.items.find((b) => b.kind === 'shop')?.code || '';

  const query = useQuery<Listing>({
    queryKey: ['passthrough-templates'],
    queryFn: () => api.get('/api/passthrough-templates'),
  });
  const refresh = (): Promise<void> =>
    client.invalidateQueries({ queryKey: ['passthrough-templates'] });

  const remove = useMutation({
    mutationFn: (i: Item) =>
      api.del(
        `/api/passthrough-templates${qs({ brand: i.brand, template: i.template, language: i.language })}`,
      ),
    onSuccess: () => {
      setDeleting(null);
      setError(null);
      void refresh();
    },
    onError: (e: Error) => {
      setDeleting(null);
      setError(e.message);
    },
  });
  const verify = useMutation({
    mutationFn: (i: Item) =>
      api.post<Verification>('/api/passthrough-templates/verify', {
        brand: i.brand === '*' ? activeBrand : i.brand,
        template: i.template,
        language: i.language,
      }),
    onSuccess: (result, item) => {
      setError(null);
      setVerified({ item, result });
    },
    onError: (e: Error) => setError(e.message),
  });

  if (query.isLoading) return <Loading what="mapování šablon" />;
  if (query.isError) {
    return (
      <ErrorState
        title="Mapování šablon se nepodařilo načíst"
        error={query.error}
        onRetry={() => void query.refetch()}
      />
    );
  }
  const data = query.data!;
  const coverage = data.coverage.filter((c) => c.brand === activeBrand);

  return (
    <>
      <Panel
        title="Vyplněnost podle značky"
        subtitle="Pro každou šablonu e-shopu musí být u každého jazyka značky vyplněno číslo e-mailu ze SmartEmailingu. Bez něj hub zprávu nepošle a e-shop pošle sám."
        action={<BrandSelect value={activeBrand} onChange={setBrand} allLabel={null} shopOnly />}
      >
        {coverage.length === 0 ? (
          <EmptyState title="Značka nemá žádné jazyky">
            Jazyky značky se nastavují v Nastavení → Značky.
          </EmptyState>
        ) : (
          <div className="scroll">
            <table data-testid="pt-coverage">
              <thead>
                <tr>
                  <th>Šablona e-shopu</th>
                  <th>Kód</th>
                  <th>Vyplněno</th>
                  <th>Chybí jazyky</th>
                </tr>
              </thead>
              <tbody>
                {coverage.map((c) => (
                  <tr key={c.template}>
                    <td>{c.template}</td>
                    <td>
                      <code>{c.code}</code>
                    </td>
                    <td>
                      <StatusBadge
                        tone={c.filled === c.expected ? 'ok' : c.filled === 0 ? 'bad' : 'warn'}
                        icon={c.filled === c.expected ? 'check' : 'alert'}
                      >
                        vyplněno {c.filled} z {c.expected} jazyků
                      </StatusBadge>
                    </td>
                    <td>{c.missing.length > 0 ? c.missing.join(', ') : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="note">
          Počítá se jen přesná shoda jazyka. Při odesílání se u chybějícího jazyka použije
          angličtina, pokud je pro ni číslo vyplněné.
        </p>
      </Panel>

      <Panel
        title="Mapování šablon"
        subtitle="Číslo e-mailu (email_id) ze SmartEmailingu pro šablonu e-shopu, značku a jazyk."
        action={
          mayWrite ? (
            <div className="row">
              <button
                type="button"
                className="btn"
                disabled={!activeBrand}
                onClick={() => setSuggesting(true)}
                data-testid="pt-suggest-open"
              >
                Navrhnout ze SmartEmailingu
              </button>
              <button type="button" className="btn" onClick={() => setImporting(true)}>
                Importovat CSV
              </button>
              <button
                type="button"
                className="btn pri"
                onClick={() => setEditing({ brand: '*', language: 'cs' })}
              >
                Přidat mapování
              </button>
            </div>
          ) : undefined
        }
      >
        {error ? <ErrorState title="Akce se nepovedla" error={new Error(error)} /> : null}
        {data.items.length === 0 ? (
          <EmptyState
            title="Zatím žádné mapování"
            action={
              mayWrite ? (
                <button type="button" className="btn" onClick={() => setImporting(true)}>
                  Importovat CSV
                </button>
              ) : undefined
            }
          >
            Bez mapování hub žádnou zprávu ve tvaru e-mailové služby neodešle. Přidejte první řádek
            nebo nahrajte soubor CSV z exportu šablon e-shopu.
          </EmptyState>
        ) : (
          <div className="scroll">
            <table data-testid="pt-table">
              <thead>
                <tr>
                  <th>Značka</th>
                  <th>Šablona e-shopu</th>
                  <th>Jazyk</th>
                  <th className="n">Číslo e-mailu</th>
                  <th>Akce</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((i) => (
                  <tr key={`${i.brand}|${i.code}|${i.language}`}>
                    <td>{brandLabel(i.brand)}</td>
                    <td>
                      {i.template} <code>{i.code}</code>
                    </td>
                    <td>{i.language}</td>
                    <td className="n">{i.emailId}</td>
                    <td>
                      <div className="row">
                        {mayWrite ? (
                          <button
                            type="button"
                            className="btn"
                            disabled={verify.isPending}
                            onClick={() => verify.mutate(i)}
                          >
                            Ověřit ve SmartEmailingu
                          </button>
                        ) : null}
                        {mayWrite ? (
                          <button type="button" className="btn" onClick={() => setEditing(i)}>
                            Upravit
                          </button>
                        ) : null}
                        {mayWrite ? (
                          <button type="button" className="btn dng" onClick={() => setDeleting(i)}>
                            Smazat
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="note">
          „Ověřit ve SmartEmailingu“ jen přečte e-mail a porovná jeho proměnné s tím, co e-shop
          posílá. Nic se neodešle a ve SmartEmailingu se nic nemění. Řádek pro všechny značky se
          ověřuje v účtu značky vybrané nahoře.
        </p>
      </Panel>

      {editing ? (
        <EditDialog
          initial={editing}
          templates={data.templates}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            setError(null);
            void refresh();
          }}
        />
      ) : null}
      {suggesting ? (
        <SuggestDialog
          brand={activeBrand}
          onClose={() => setSuggesting(false)}
          onSaved={() => {
            void refresh();
          }}
        />
      ) : null}
      {importing ? (
        <ImportDialog
          onClose={() => setImporting(false)}
          onSaved={() => {
            void refresh();
          }}
        />
      ) : null}
      <ConfirmDialog
        open={deleting !== null}
        danger
        title="Smazat mapování?"
        confirmLabel="Smazat mapování"
        busy={remove.isPending}
        onCancel={() => setDeleting(null)}
        onConfirm={() => deleting && remove.mutate(deleting)}
      >
        <p>
          Šablona {deleting?.template}, jazyk {deleting?.language},{' '}
          {brandLabel(deleting?.brand ?? '')}. Zprávy, které ji potřebují, se pak zadrží a e-shop
          pošle sám. Změna se zapíše do auditu.
        </p>
      </ConfirmDialog>
      <Dialog
        open={verified !== null}
        wide
        title="Ověření šablony ve SmartEmailingu"
        testId="pt-verify"
        onClose={() => setVerified(null)}
      >
        {verified ? <VerifyResult item={verified.item} result={verified.result} /> : null}
      </Dialog>
    </>
  );
}

function VerifyResult({ item, result }: { item: Item; result: Verification }): JSX.Element {
  const list = (xs: string[]): JSX.Element =>
    xs.length === 0 ? (
      <span>—</span>
    ) : (
      <span>
        {xs.map((x, i) => (
          <span key={x}>
            {i > 0 ? ', ' : ''}
            <code>{x}</code>
          </span>
        ))}
      </span>
    );
  return (
    <div className="stack" data-testid="pt-verify-result">
      <p>
        Šablona {item.template}, jazyk {item.language}, číslo e-mailu {result.emailId}.
      </p>
      {!result.exists ? (
        <StatusBadge tone="bad" icon="alert">
          e-mail ve SmartEmailingu nenalezen
        </StatusBadge>
      ) : (
        <>
          <p>
            {result.ok ? (
              <StatusBadge tone="ok" icon="check">
                v pořádku
              </StatusBadge>
            ) : (
              <StatusBadge tone="bad" icon="alert">
                šablona používá proměnné, které e-shop neposílá
              </StatusBadge>
            )}
          </p>
          <p>
            <b>Předmět:</b> {result.subject ?? '—'}
          </p>
          <p>
            <b>Chybějící proměnné</b> (šablona je používá, e-shop je neposílá):{' '}
            {list(result.missing)}
          </p>
          <p>
            <b>Nevyužité proměnné</b> (e-shop je posílá, šablona je nepoužívá):{' '}
            {list(result.unused)}
          </p>
          {result.missingOneOf ? (
            <p>
              <StatusBadge tone="warn" icon="alert">
                upozornění
              </StatusBadge>{' '}
              Šablona nepoužívá žádný z odkazů {list(result.missingOneOf)}.
            </p>
          ) : null}
          <p>
            <b>Použité proměnné:</b> {list(result.used)}
          </p>
        </>
      )}
      <p className="note">Proměnné se hledají ve tvaru {'{{název}}'}; ověření nic neodesílá.</p>
    </div>
  );
}

function EditDialog({
  initial,
  templates,
  onClose,
  onSaved,
}: {
  initial: Partial<Item>;
  templates: Array<{ template: string; code: string }>;
  onClose: () => void;
  onSaved: () => void;
}): JSX.Element {
  const brands = useBrands();
  const isNew = initial.template === undefined;
  const [brand, setBrand] = useState(initial.brand ?? '*');
  const [template, setTemplate] = useState(initial.template ?? templates[0]?.template ?? '');
  const [language, setLanguage] = useState(initial.language ?? 'cs');
  const [emailId, setEmailId] = useState(initial.emailId ? String(initial.emailId) : '');
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () =>
      api.put('/api/passthrough-templates', {
        brand,
        template,
        language: language.trim().toLowerCase(),
        emailId: Number(emailId),
      }),
    onSuccess: onSaved,
    onError: (e: Error) => setError(e.message),
  });
  const valid = /^[a-z]{2}$/.test(language.trim().toLowerCase()) && /^[1-9]\d*$/.test(emailId);
  return (
    <Dialog
      open
      title={isNew ? 'Přidat mapování šablony' : 'Upravit mapování šablony'}
      testId="pt-edit"
      onClose={onClose}
    >
      <div className="stack">
        <Field label="Značka">
          <select
            value={brand}
            disabled={!isNew}
            onChange={(e) => setBrand(e.target.value)}
            data-testid="pt-brand"
          >
            <option value="*">Všechny značky</option>
            {(brands.data?.items ?? []).map((b) => (
              <option key={b.code} value={b.code}>
                {b.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Šablona e-shopu">
          <select
            value={template}
            disabled={!isNew}
            onChange={(e) => setTemplate(e.target.value)}
            data-testid="pt-template"
          >
            {templates.map((t) => (
              <option key={t.template} value={t.template}>
                {t.template} ({t.code})
              </option>
            ))}
          </select>
        </Field>
        <Field label="Jazyk" hint="Dvě malá písmena, například cs nebo en.">
          <input
            value={language}
            readOnly={!isNew}
            maxLength={2}
            onChange={(e) => setLanguage(e.target.value)}
            data-testid="pt-language"
          />
        </Field>
        <Field label="Číslo e-mailu (email_id) ze SmartEmailingu" hint="Kladné celé číslo.">
          <input
            inputMode="numeric"
            value={emailId}
            onChange={(e) => setEmailId(e.target.value)}
            data-testid="pt-email-id"
          />
        </Field>
        {error ? (
          <ErrorState title="Mapování se nepodařilo uložit" error={new Error(error)} />
        ) : null}
        <div className="row dlg-actions">
          <button type="button" className="btn" onClick={onClose}>
            Zrušit
          </button>
          <button
            type="button"
            className="btn pri"
            disabled={!valid || save.isPending}
            onClick={() => save.mutate()}
            data-testid="pt-save"
          >
            Uložit
          </button>
        </div>
      </div>
    </Dialog>
  );
}

interface Suggestion {
  template: string;
  code: string;
  language: string;
  candidates: Array<{ emailId: number; name: string; score: number; languageMatched: boolean }>;
}

/** Návrh čísel e-mailů ze SmartEmailingu podle názvu; ukládá se až po potvrzení, jen do mapování v hubu. */
function SuggestDialog({
  brand,
  onClose,
  onSaved,
}: {
  brand: string;
  onClose: () => void;
  onSaved: () => void;
}): JSX.Element {
  const [error, setError] = useState<string | null>(null);
  const [picked, setPicked] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState(0);
  const key = (s: Suggestion): string => `${s.code}|${s.language}`;
  const query = useQuery<{ scanned: number; truncated: boolean; suggestions: Suggestion[] }>({
    queryKey: ['passthrough-suggest', brand],
    queryFn: async () => {
      const r = await api.post<{ scanned: number; truncated: boolean; suggestions: Suggestion[] }>(
        '/api/passthrough-templates/suggest',
        { brand },
      );
      setPicked(
        Object.fromEntries(
          r.suggestions
            .filter((s) => s.candidates[0]?.languageMatched)
            .map((s) => [key(s), String(s.candidates[0]!.emailId)]),
        ),
      );
      return r;
    },
    staleTime: Infinity,
    retry: false,
  });
  const save = useMutation({
    mutationFn: async () => {
      let n = 0;
      for (const s of query.data?.suggestions ?? []) {
        const id = picked[key(s)];
        if (!id) continue;
        await api.put('/api/passthrough-templates', {
          brand,
          template: s.template,
          language: s.language,
          emailId: Number(id),
        });
        n += 1;
      }
      return n;
    },
    onSuccess: (n) => {
      setError(null);
      setSaved(n);
      onSaved();
    },
    onError: (e: Error) => setError(e.message),
  });
  const rows = query.data?.suggestions ?? [];
  const chosen = Object.values(picked).filter(Boolean).length;
  return (
    <Dialog
      open
      wide
      title={`Návrh čísel e-mailů ze SmartEmailingu (${brand})`}
      testId="pt-suggest"
      onClose={onClose}
    >
      <div className="stack">
        {query.isFetching ? (
          <Loading what="e-maily ze SmartEmailingu (jen čtení, může to trvat desítky vteřin)" />
        ) : null}
        {query.isError ? (
          <ErrorState
            title="Návrh se nepodařil"
            error={query.error as Error}
            onRetry={() => void query.refetch()}
          />
        ) : null}
        {query.data ? (
          <>
            <p className="note">
              Prohledáno {query.data.scanned} e-mailů podle názvu
              {query.data.truncated ? ' (výpis byl zkrácen, část e-mailů se neprohledala)' : ''}.
              Nic se neuloží, dokud nepotvrdíte. Ve SmartEmailingu se nic nemění.
            </p>
            {rows.length === 0 ? (
              <EmptyState title="Nic k doplnění">U této značky už je vyplněno všechno.</EmptyState>
            ) : (
              <div className="scroll">
                <table data-testid="pt-suggest-table">
                  <thead>
                    <tr>
                      <th>Šablona e-shopu</th>
                      <th>Jazyk</th>
                      <th>E-mail ve SmartEmailingu</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((s) => (
                      <tr key={key(s)}>
                        <td>
                          {s.template} <code>{s.code}</code>
                        </td>
                        <td>{s.language}</td>
                        <td>
                          {s.candidates.length === 0 ? (
                            <span className="muted">nic nenalezeno, doplňte ručně</span>
                          ) : (
                            <select
                              value={picked[key(s)] ?? ''}
                              aria-label={`E-mail pro ${s.template} ${s.language}`}
                              onChange={(e) => setPicked({ ...picked, [key(s)]: e.target.value })}
                            >
                              <option value="">— nepřiřadit —</option>
                              {s.candidates.map((c) => (
                                <option key={c.emailId} value={String(c.emailId)}>
                                  {c.emailId} · {c.name}
                                  {c.languageMatched ? '' : ' (jazyk nezjištěn)'}
                                </option>
                              ))}
                            </select>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        ) : null}
        {error ? <ErrorState title="Uložení se nepovedlo" error={new Error(error)} /> : null}
        {saved > 0 ? (
          <StatusBadge tone="ok" icon="check">
            uloženo {saved} mapování
          </StatusBadge>
        ) : null}
        <div className="row dlg-actions">
          <button type="button" className="btn" onClick={onClose}>
            Zavřít
          </button>
          <button
            type="button"
            className="btn pri"
            disabled={chosen === 0 || save.isPending}
            onClick={() => save.mutate()}
            data-testid="pt-suggest-save"
          >
            Uložit vybrané ({chosen})
          </button>
        </div>
      </div>
    </Dialog>
  );
}

function ImportDialog({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved: () => void;
}): JSX.Element {
  const [csv, setCsv] = useState('');
  const [preview, setPreview] = useState<{
    rows: CsvRow[];
    saved: number;
    applied: boolean;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const run = useMutation({
    mutationFn: (apply: boolean) =>
      api.post<{ rows: CsvRow[]; saved: number; applied: boolean }>(
        '/api/passthrough-templates/import',
        { csv, apply },
      ),
    onSuccess: (r) => {
      setError(null);
      setPreview(r);
      if (r.applied) onSaved();
    },
    onError: (e: Error) => setError(e.message),
  });
  const savable = preview?.rows.filter((r) => r.status === 'new' || r.status === 'changed') ?? [];
  const onFile = async (file: File | undefined): Promise<void> => {
    if (!file) return;
    setCsv(await file.text());
    setPreview(null);
  };
  return (
    <Dialog open wide title="Import mapování z CSV" testId="pt-import" onClose={onClose}>
      <div className="stack">
        <p>
          Soubor má hlavičku <code>template,language,email_id</code> a volitelně <code>brand</code>.
          Šablona je například <code>OrderPaid</code>. Nejdřív uvidíte náhled, nic se neuloží, dokud
          to nepotvrdíte.
        </p>
        <Field label="Soubor CSV">
          <input
            type="file"
            accept=".csv,text/csv"
            data-testid="pt-file"
            onChange={(e) => void onFile(e.target.files?.[0])}
          />
        </Field>
        <Field label="Nebo vložte text">
          <textarea
            rows={6}
            value={csv}
            data-testid="pt-csv"
            onChange={(e) => {
              setCsv(e.target.value);
              setPreview(null);
            }}
          />
        </Field>
        {error ? (
          <ErrorState title="Soubor se nepodařilo zpracovat" error={new Error(error)} />
        ) : null}
        {preview ? (
          <div className="scroll">
            <table data-testid="pt-import-rows">
              <thead>
                <tr>
                  <th className="n">Řádek</th>
                  <th>Značka</th>
                  <th>Šablona</th>
                  <th>Jazyk</th>
                  <th className="n">Číslo e-mailu</th>
                  <th>Výsledek</th>
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((r) => (
                  <tr key={r.row}>
                    <td className="n">{r.row}</td>
                    <td>{brandLabel(r.brand)}</td>
                    <td>{r.template || '—'}</td>
                    <td>{r.language || '—'}</td>
                    <td className="n">{r.emailId ?? '—'}</td>
                    <td>
                      <StatusBadge
                        tone={ROW_STATUS[r.status].tone}
                        icon={r.status === 'error' ? 'alert' : 'check'}
                      >
                        {ROW_STATUS[r.status].text}
                      </StatusBadge>
                      {r.error ? <div className="muted">{r.error}</div> : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
        {preview?.applied ? (
          <p role="status" data-testid="pt-import-done">
            Uloženo {preview.saved} řádků. Řádky s chybou se neuložily.
          </p>
        ) : null}
        <div className="row dlg-actions">
          <button type="button" className="btn" onClick={onClose}>
            Zavřít
          </button>
          <button
            type="button"
            className="btn"
            disabled={csv.trim() === '' || run.isPending}
            onClick={() => run.mutate(false)}
            data-testid="pt-preview"
          >
            Zkontrolovat
          </button>
          <button
            type="button"
            className="btn pri"
            disabled={!preview || preview.applied || savable.length === 0 || run.isPending}
            onClick={() => run.mutate(true)}
            data-testid="pt-apply"
          >
            Uložit platné řádky ({savable.length})
          </button>
        </div>
      </div>
    </Dialog>
  );
}
